// ═══════════════════════════════════════════════════════════════════════
// Dispositifs sociaux — actes de gestion d'une créance (FINANCE) : suspension, reprise, report, rééchelonnement,
// PROPOSÉS par une personne puis VALIDÉS ou rejetés par une autre (jamais le salarié concerné). Partagés par l'état de
// campagne et la fiche d'une créance. Les actes en attente sont lus dans `actes_en_attente` du détail de la créance
// quand le serveur les renvoie (DM-WEB-003 n° 4) ; sinon la référence de la proposition est saisie.
// `ligne` = { salarie, numero, creance: { id, solde, etat } }.
// Routes : POST /api/dispositifs/finance/creances/:id/actes, POST /actes/:id/decider (Idempotency-Key).
// ═══════════════════════════════════════════════════════════════════════
import { useState } from 'react';
import { FileSignature, Wrench } from 'lucide-react';
import { Squelette } from '../../design/composants';
import { toArray, libelle } from './api';
import { Champ, ErreurServeur } from './commun';
import { useSocial, francs, moisLong, periodeCourante, periodeDecalee, ETATS_CREANCE } from './apiSocial';
import { useLectureSociale, FenetreActe, motifValide, ChampMotif, ChoixRadio } from './communSocial';
import { useCampagnes, ACTES_CREANCE } from './apiCampagnes';
import { lireEntier } from './communCampagnes';

/** Résumé lisible d'un acte proposé (type et paramètres). */
export function resumeActe(a) {
  const p = a.proposition || a;
  const type = a.type || p.type;
  const details = [
    p.nb_mois ? `${p.nb_mois} mois` : null,
    p.nb_echeances ? `${p.nb_echeances} échéance(s) à partir de ${moisLong(p.premiere_periode)}` : null,
  ].filter(Boolean).join(', ');
  return `${libelle(ACTES_CREANCE, type)}${details ? ` (${details})` : ''}`;
}

// ─── Actes de gestion d'une créance (FINANCE) ─────────────────────────────────────────────────
export function FenetreProposerActe({ ligne, onFermer, onFait }) {
  const api = useCampagnes();
  const suspendue = ligne.creance.etat === 'SUSPENDUE';
  const [type, setType] = useState(suspendue ? 'REPRISE' : 'SUSPENSION');
  const [nbMois, setNbMois] = useState('1');
  const [nbEch, setNbEch] = useState('');
  const [premiere, setPremiere] = useState(periodeDecalee(periodeCourante(), 1));
  const [motif, setMotif] = useState('');
  const n = lireEntier(nbMois);
  const e = lireEntier(nbEch);
  const params = type === 'SUSPENSION' || type === 'REPORT' ? (n && n >= 1 && n <= 24) : type === 'REECHELONNEMENT' ? (e && e >= 1 && /^\d{4}-\d{2}$/.test(premiere)) : true;
  const options = suspendue ? { REPRISE: ACTES_CREANCE.REPRISE, REPORT: ACTES_CREANCE.REPORT, REECHELONNEMENT: ACTES_CREANCE.REECHELONNEMENT }
    : { SUSPENSION: ACTES_CREANCE.SUSPENSION, REPORT: ACTES_CREANCE.REPORT, REECHELONNEMENT: ACTES_CREANCE.REECHELONNEMENT };
  return (
    <FenetreActe titre={`Proposer un acte — ${ligne.salarie}`} Icone={Wrench} libelleBouton="Proposer" pret={!!(params && motifValide(motif, 3))} onFermer={onFermer}
      aide="L'acte est proposé ici puis validé par une autre personne ; il ne s'applique qu'à la validation, par une nouvelle version de l'échéancier (le passé n'est jamais réécrit). Une seule proposition en attente par créance."
      onConfirmer={async (cle) => {
        const corps = { type, motif: motif.trim(),
          ...(type === 'SUSPENSION' || type === 'REPORT' ? { nb_mois: n } : {}), ...(type === 'REECHELONNEMENT' ? { nb_echeances: e, premiere_periode: premiere } : {}) };
        const r = await api.proposerActeCreance(ligne.creance.id, corps, cle);
        onFait(`${libelle(ACTES_CREANCE, type)} proposé${toArray(r?.apercu).length ? ` (${toArray(r.apercu).length} échéance(s) dans l'aperçu)` : ''} : référence ${String(r?.decision_id || '').slice(0, 8)}, à valider par une autre personne.`);
      }}>
      <p style={{ margin: '0 0 12px', fontSize: 14 }}>Créance {ligne.numero} · restant dû {francs(ligne.creance.solde)} · {libelle(ETATS_CREANCE, ligne.creance.etat)}</p>
      <ChoixRadio nom="acte-creance" options={options} valeur={type} onChange={setType} />
      {(type === 'SUSPENSION' || type === 'REPORT') && (
        <Champ libelle="Nombre de mois" requis aide="De 1 à 24 mois : les échéances à venir sont décalées d'autant."><input className="rh-champ-saisie" inputMode="numeric" value={nbMois} onChange={(x) => setNbMois(x.target.value)} style={{ maxWidth: 120 }} /></Champ>
      )}
      {type === 'REECHELONNEMENT' && (
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(150px, 1fr))', gap: 12 }}>
          <Champ libelle="Nombre d'échéances" requis><input className="rh-champ-saisie" inputMode="numeric" value={nbEch} onChange={(x) => setNbEch(x.target.value)} /></Champ>
          <Champ libelle="Premier mois" requis aide={moisLong(premiere)}><input type="month" className="rh-champ-saisie" value={premiere} onChange={(x) => setPremiere(x.target.value)} /></Champ>
        </div>
      )}
      <ChampMotif valeur={motif} onChange={setMotif} aide="3 caractères au moins. Le motif est conservé dans l'historique." />
    </FenetreActe>
  );
}

/** Décision d'un acte proposé : liste du serveur si elle est fournie, sinon référence communiquée par le proposant. */
export function FenetreDeciderActe({ ligne, decisionId: initiale = '', onFermer, onFait }) {
  const api = useCampagnes();
  const social = useSocial();
  const cr = useLectureSociale(() => social.creance(ligne.creance.id), [social, ligne.creance.id]);
  const enAttente = toArray(cr.donnees?.actes_en_attente);
  const [choix, setChoix] = useState(initiale);
  const [reference, setReference] = useState('');
  const [valider, setValider] = useState('VALIDER');
  const [motif, setMotif] = useState('');
  const decisionId = enAttente.length ? choix : reference.trim();
  const refOk = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i.test(decisionId);
  return (
    <FenetreActe titre={`Décider d'un acte — ${ligne.salarie}`} Icone={FileSignature} libelleBouton={valider === 'VALIDER' ? 'Valider et appliquer' : 'Rejeter'} danger={valider !== 'VALIDER'}
      pret={refOk && motifValide(motif, 3)} onFermer={onFermer}
      aide="La personne qui a proposé l'acte ne le valide pas, et le salarié concerné non plus. La validation crée une nouvelle version de l'échéancier."
      onConfirmer={async (cle) => { await api.deciderActeCreance(decisionId, { decision: valider, motif: motif.trim() }, cle); onFait(valider === 'VALIDER' ? 'Acte validé et appliqué.' : 'Acte rejeté.'); }}>
      {!cr.donnees && !cr.erreur && <Squelette largeur="50%" />}
      {cr.erreur && <ErreurServeur erreur={cr.erreur} />}
      {cr.donnees && (enAttente.length ? (
        <Champ libelle="Acte en attente" requis>
          <select className="rh-champ-saisie" value={choix} onChange={(x) => setChoix(x.target.value)}>
            <option value="">Choisir…</option>
            {enAttente.map((a) => <option key={a.decision_id} value={a.decision_id}>{resumeActe(a)}{a.propose_par ? ` — proposé par ${a.propose_par}` : ''}</option>)}
          </select>
        </Champ>
      ) : (
        <Champ libelle="Référence de la proposition" requis aide="Le serveur ne liste pas encore les actes en attente d'une créance (demande en cours) : saisissez la référence complète communiquée par la personne qui a proposé l'acte.">
          <input className="rh-champ-saisie" value={reference} onChange={(x) => setReference(x.target.value)} />
        </Champ>
      ))}
      <ChoixRadio nom="decision-acte" options={{ VALIDER: 'Valider', REJETER: 'Rejeter' }} valeur={valider} onChange={setValider} />
      <ChampMotif valeur={motif} onChange={setMotif} aide="3 caractères au moins." />
    </FenetreActe>
  );
}
